// Sidecar-backed parcel attribute lookup.
//
// The LULC demo used to load 4 multi-MB GeoJSONs and index them by
// `cadastre_polygon_pid` so the parcel detail panel could pull the
// `breakdown` JSON column for every parcel on demand. With the PMTiles
// migration the per-feature breakdowns no longer ship inside the vector
// tiles (tippecanoe strips them — they roughly double tile size for zero
// rendering value). Instead the build pipeline emits a single pid-keyed
// JSON sidecar at `/demos/static/pmtiles/parcel_attributes.json` (~7-8 MB
// uncompressed, ~1 MB gzipped over CloudFront); we fetch it once in the
// background and answer every detail-panel query off the in-memory map.

import type { LayerFeatureData } from "../types/agricultural-lulc";

const SIDECAR_URL = "/demos/static/pmtiles/parcel_attributes.json";

interface ParcelAttributeEntry {
  lulc2024: string | null;
  lulc2025: string | null;
  crops: string | null;
  forest: string | null;
  area: number | null;
}

let sidecar: Record<string, ParcelAttributeEntry> | null = null;
let sidecarPromise: Promise<Record<string, ParcelAttributeEntry>> | null = null;

// Kick off a single fetch of the parcel attribute sidecar. Idempotent: every
// caller after the first awaits the same in-flight promise. Subsequent
// synchronous lookups via `queryLayerDataForParcel` hit the cached `sidecar`.
export function ensureParcelAttributes(): Promise<
  Record<string, ParcelAttributeEntry>
> {
  if (sidecar) return Promise.resolve(sidecar);
  if (sidecarPromise) return sidecarPromise;
  sidecarPromise = fetch(SIDECAR_URL)
    .then((res) => {
      if (!res.ok) {
        throw new Error(
          `parcel-attributes sidecar fetch failed: ${res.status} ${res.statusText}`
        );
      }
      return res.json();
    })
    .then((data: Record<string, ParcelAttributeEntry>) => {
      sidecar = data;
      return data;
    })
    .catch((err) => {
      console.error("[lulc-demo] parcel attribute sidecar load failed:", err);
      sidecar = {};
      return sidecar;
    });
  return sidecarPromise;
}

// Featured-parcel area lookup. Used to pre-populate the detail panel for the
// hard-coded `FEATURED_PARCEL_ID` on the parcel wizard step. The PMTiles
// vector source can't be queried for an arbitrary pid that isn't currently in
// the viewport, so we read the area off the sidecar instead.
export function getFeaturedParcelArea(pid: string): number {
  return sidecar?.[pid]?.area ?? 0;
}

// Pulls the four breakdown JSON strings for a parcel from the sidecar.
// Returns undefined fields if the sidecar hasn't loaded yet — the detail
// panel renders empty sections rather than throwing.
export function queryLayerDataForParcel(pid: string): LayerFeatureData {
  const entry = sidecar?.[pid];
  return {
    lulc2024Breakdown: entry?.lulc2024 ?? undefined,
    lulc2025Breakdown: entry?.lulc2025 ?? undefined,
    cropsBreakdown: entry?.crops ?? undefined,
    forestBreakdown: entry?.forest ?? undefined,
  };
}
